import { datadogRum } from '@datadog/browser-rum';
import {
  type AnonymousLaunchDarklyContext,
  formatLaunchDarklyUserContext,
  type UserLaunchDarklyContext,
} from '@side/launch-darkly';
import { useSession } from '@side/next-auth/react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { IS_RUNNING_IN_CYPRESS } from '@/helpers/cypress-helpers';
import { useIdentity } from '$api-clients/hooks';
import { subscribedFlags } from '$launch-darkly/app-flags';

type LaunchDarklyProps = {
  children?: ReactNode;
};

/**
 * Initialize the Launch Darkly React SDK
 */
export function LaunchDarklyProvider(props: LaunchDarklyProps) {
  const { status, data: session } = useSession();
  const { data, isLoading } = useIdentity(
    '/auth/userinfo',
    // Skip fetching user info if session is loading or doesn't exist
    status === 'loading' || !session ? null : undefined,
  );

  // NOTE: Rendering while session is still loading causes crash
  if (status === 'loading' || isLoading) {
    return null;
  }

  let context: UserLaunchDarklyContext | AnonymousLaunchDarklyContext = {
    key: 'unknown',
    kind: 'user',
  };

  if (data) {
    context = formatLaunchDarklyUserContext(data);
  }

  return (
    <LDProvider
      clientSideID={process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_SIDE_ID ?? ''}
      context={context}
      flags={subscribedFlags}
      /**
       * In Cypress tests where we want to test flag on/off in the same instance
       * having a previous value restored is very unhelpful.
       *
       * Disabling streaming in cypress helps quiet down cypress logs.
       */
      options={{
        streaming: !IS_RUNNING_IN_CYPRESS,
        ...(!IS_RUNNING_IN_CYPRESS && {
          bootstrap: 'localStorage',
          inspectors: [
            {
              type: 'flag-used',
              name: 'dd-inspector',
              method: (key, detail) => {
                datadogRum.addFeatureFlagEvaluation(key, detail.value);
              },
            },
          ],
        }),
      }}
      {...props}
    />
  );
}
